import { z } from 'zod';
import { ActionSchema } from '../actions';

/**
 * Widget block schemas (PLATFORM_EXPANSION.md §3, X0.1's widget-schemas line).
 * These are the shared contract B builds the React halves against; the server
 * SVG half (A) renders the same props underneath. Provider-agnostic per D0:
 * a data widget references a normalised `data_key` (fed by a connector or the
 * existing ingest path) and NEVER a provider name, URL or key. Attribution
 * lives in the connector registry, not here.
 *
 * Every widget is `tablet_only` until its server SVG renderer exists (the
 * badge is carried in the capability table, `packages/layout`).
 */

const rect = z.object({
  x: z.number().int().nonnegative(),
  y: z.number().int().nonnegative(),
  w: z.number().int().positive(),
  h: z.number().int().positive()
});
const on = z.object({ tap: z.array(ActionSchema).optional() }).optional();

/** A normalised data binding: the widget reads `data_points[key]`, provider-agnostic. */
export const DataSourceRefSchema = z.object({
  data_key: z.string().min(1).max(120)
});
export type DataSourceRef = z.infer<typeof DataSourceRefSchema>;

// ---- clock ---------------------------------------------------------------
export const CLOCK_STYLES = ['digital', 'analog', 'flip', 'minimal', 'word'] as const;

export const ClockWidgetBlockSchema = z.object({
  id: z.string().min(1),
  type: z.literal('clock'),
  rect,
  on,
  props: z.object({
    style: z.enum(CLOCK_STYLES).default('digital'),
    format: z.enum(['12h', '24h']).default('24h'),
    show_seconds: z.boolean().default(false),
    show_date: z.boolean().default(true),
    date_format: z.string().max(40).optional(),
    show_sun: z.boolean().default(false),
    world_clocks: z.array(z.object({ label: z.string().min(1).max(40), timezone: z.string().min(1) })).default([]),
    countdown: z.object({ label: z.string().min(1).max(60), to: z.string().datetime() }).optional()
  })
});
export type ClockWidgetBlock = z.infer<typeof ClockWidgetBlockSchema>;

// ---- weather -------------------------------------------------------------
export const WeatherWidgetBlockSchema = z.object({
  id: z.string().min(1),
  type: z.literal('weather'),
  rect,
  on,
  props: z.object({
    source: DataSourceRefSchema,
    layout: z.enum(['now', 'hourly', 'daily']).default('now'),
    units: z.enum(['metric', 'imperial']).default('metric'),
    days: z.number().int().min(1).max(7).default(5),
    show_uv: z.boolean().default(false),
    show_rain: z.boolean().default(true),
    show_wind: z.boolean().default(false),
    location_label: z.string().max(60).optional()
  })
});
export type WeatherWidgetBlock = z.infer<typeof WeatherWidgetBlockSchema>;

// ---- news ----------------------------------------------------------------
export const NewsWidgetBlockSchema = z.object({
  id: z.string().min(1),
  type: z.literal('news'),
  rect,
  on,
  props: z.object({
    source: DataSourceRefSchema,
    mode: z.enum(['list', 'ticker']).default('list'),
    max_items: z.number().int().min(1).max(30).default(8),
    show_source: z.boolean().default(true),
    show_time: z.boolean().default(false),
    speed_s: z.number().min(5).max(120).default(30)
  })
});
export type NewsWidgetBlock = z.infer<typeof NewsWidgetBlockSchema>;

// ---- sport ---------------------------------------------------------------
export const SportWidgetBlockSchema = z.object({
  id: z.string().min(1),
  type: z.literal('sport'),
  rect,
  on,
  props: z.object({
    source: DataSourceRefSchema,
    mode: z.enum(['fixtures', 'live', 'results', 'table']).default('fixtures'),
    max_items: z.number().int().min(1).max(20).default(6),
    competition_label: z.string().max(80).optional(),
    show_logo: z.boolean().default(false)
  })
});
export type SportWidgetBlock = z.infer<typeof SportWidgetBlockSchema>;

// ---- solar ---------------------------------------------------------------
export const SolarWidgetBlockSchema = z.object({
  id: z.string().min(1),
  type: z.literal('solar'),
  rect,
  on,
  props: z.object({
    source: DataSourceRefSchema,
    mode: z.enum(['forecast', 'actual', 'both']).default('forecast'),
    horizon: z.enum(['today', 'week']).default('today'),
    units: z.enum(['kwh', 'kw']).default('kwh'),
    show_peak: z.boolean().default(true)
  })
});
export type SolarWidgetBlock = z.infer<typeof SolarWidgetBlockSchema>;

// ---- calendar ------------------------------------------------------------
export const CalendarWidgetBlockSchema = z.object({
  id: z.string().min(1),
  type: z.literal('calendar'),
  rect,
  on,
  props: z.object({
    source: DataSourceRefSchema,
    mode: z.enum(['agenda', 'day', 'week']).default('agenda'),
    max_events: z.number().int().min(1).max(20).default(5),
    show_location: z.boolean().default(false),
    show_attendees: z.boolean().default(false)
  })
});
export type CalendarWidgetBlock = z.infer<typeof CalendarWidgetBlockSchema>;

// ---- photo slideshow -----------------------------------------------------
export const SlideshowWidgetBlockSchema = z.object({
  id: z.string().min(1),
  type: z.literal('slideshow'),
  rect,
  on,
  props: z.object({
    media_ids: z.array(z.string().min(1)).min(1),
    transition: z.enum(['none', 'fade', 'slide', 'kenburns']).default('fade'),
    duration_s: z.number().int().min(1).max(3600).default(8),
    kenburns: z.boolean().default(true),
    caption: z.boolean().default(false),
    clock_overlay: z.boolean().default(false)
  })
});
export type SlideshowWidgetBlock = z.infer<typeof SlideshowWidgetBlockSchema>;

// ---- departures (UK) -----------------------------------------------------
export const DeparturesWidgetBlockSchema = z.object({
  id: z.string().min(1),
  type: z.literal('departures'),
  rect,
  on,
  props: z.object({
    source: DataSourceRefSchema,
    mode: z.enum(['bus', 'rail', 'tram']).default('bus'),
    stop_label: z.string().max(80).optional(),
    max_rows: z.number().int().min(1).max(12).default(6),
    show_platform: z.boolean().default(true),
    show_operator: z.boolean().default(false)
  })
});
export type DeparturesWidgetBlock = z.infer<typeof DeparturesWidgetBlockSchema>;

// ---- energy price (UK) ---------------------------------------------------
export const EnergyWidgetBlockSchema = z.object({
  id: z.string().min(1),
  type: z.literal('energy'),
  rect,
  on,
  props: z.object({
    source: DataSourceRefSchema,
    show_next: z.boolean().default(true),
    unit_label: z.string().max(20).default('p/kWh'),
    thresholds: z.array(z.object({
      gte: z.number(),
      colour: z.string().min(1)
    })).default([])
  })
});
export type EnergyWidgetBlock = z.infer<typeof EnergyWidgetBlockSchema>;

// ---- notice / quote / message -------------------------------------------
export const NoticeWidgetBlockSchema = z.object({
  id: z.string().min(1),
  type: z.literal('notice'),
  rect,
  on,
  props: z.object({
    messages: z.array(z.object({
      text: z.string().min(1).max(2000),
      weight: z.number().int().min(1).max(10).default(1)
    })).min(1),
    rotate_s: z.number().int().min(1).max(3600).default(15),
    align: z.enum(['left', 'center', 'right']).default('center'),
    size: z.enum(['sm', 'md', 'lg', 'xl']).default('lg')
  })
});
export type NoticeWidgetBlock = z.infer<typeof NoticeWidgetBlockSchema>;

// ---- qr (D6: uQR, MIT, vendored) ----------------------------------------
export const QrWidgetBlockSchema = z.object({
  id: z.string().min(1),
  type: z.literal('qr'),
  rect,
  on,
  props: z.object({
    /** The encoded value, e.g. a screen URL or upload link. */
    payload: z.string().min(1).max(2000),
    label: z.string().max(120).optional(),
    error_correction: z.enum(['L', 'M', 'Q', 'H']).default('M'),
    /** Rendered square side in grid cells (both renderers must agree). */
    side: z.number().int().min(1).max(12).default(4)
  })
});
export type QrWidgetBlock = z.infer<typeof QrWidgetBlockSchema>;

export const WIDGET_BLOCK_SCHEMAS = [
  ClockWidgetBlockSchema,
  WeatherWidgetBlockSchema,
  NewsWidgetBlockSchema,
  SportWidgetBlockSchema,
  SolarWidgetBlockSchema,
  CalendarWidgetBlockSchema,
  SlideshowWidgetBlockSchema,
  DeparturesWidgetBlockSchema,
  EnergyWidgetBlockSchema,
  NoticeWidgetBlockSchema,
  QrWidgetBlockSchema
] as const;

export const WIDGET_BLOCK_TYPES = [
  'clock',
  'weather',
  'news',
  'sport',
  'solar',
  'calendar',
  'slideshow',
  'departures',
  'energy',
  'notice',
  'qr'
] as const;
export type WidgetBlockType = typeof WIDGET_BLOCK_TYPES[number];
